import type { Reference, StoreObject } from '@apollo/client'
import type { ModifierDetails } from '@apollo/client/cache'

import { graphql } from '~publish/graphql'
import { removeDraftFromDraftsLists } from '~publish/helpers/drafts/removeDraftFromDraftsLists'
import type { ResultOf, VariablesOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'

import { useTypedMutation } from '../../hooks/useTypedMutation'

// Cache-modifier view of a posts connection edge: `node` is a normalized
// cache reference read through `readField`, not the full wire Post type.
type PostsEdge = {
  node: Reference | StoreObject
}

export const DeletePost = graphql(/* GraphQL */ `
  mutation DeletePost($id: PostId!) {
    deletePostV2(input: { id: $id }) {
      __typename
      ... on MutationError {
        message
      }
      ... on DeletePostSuccess {
        id
      }
    }
  }
`)

type UseDeletePost = ReturnType<
  typeof useTypedMutation<
    'DeletePostSuccess',
    ResultOf<typeof DeletePost>['deletePostV2'],
    ResultOf<typeof DeletePost>,
    VariablesOf<typeof DeletePost>
  >
>

export const useDeletePost = (): UseDeletePost =>
  useTypedMutation(DeletePost, (data) => data.deletePostV2, {
    successTypename: 'DeletePostSuccess',
    optimisticResponse: (variables) => ({
      deletePostV2: {
        __typename: 'DeletePostSuccess' as const,
        id: variables.id,
      },
    }),
    update: (cache, { data }, { variables }) => {
      const result = data?.deletePostV2
      if (!isOfType(result, 'DeletePostSuccess')) {
        return
      }

      const deletedPostId = variables?.id
      if (!deletedPostId) {
        return
      }

      // Remove post from all posts lists
      cache.modify({
        fields: {
          posts(
            existingPostsConnection: Reference | StoreObject,
            { readField }: ModifierDetails,
          ) {
            if (existingPostsConnection && '__ref' in existingPostsConnection) {
              return existingPostsConnection
            }

            const edges =
              readField<PostsEdge[]>('edges', existingPostsConnection) || []

            const filteredEdges = edges.filter(
              (edge: PostsEdge) => readField('id', edge.node) !== deletedPostId,
            )

            return {
              ...existingPostsConnection,
              edges: filteredEdges,
            }
          },
        },
      })
      removeDraftFromDraftsLists(cache, deletedPostId)

      cache.gc()
    },
  })
